import { useAppSelector } from 'hooks/redux';
import type { OrderedMap } from 'immutable';
import React, { memo, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditNote from '@mui/icons-material/EditNote';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoIcon from '@mui/icons-material/Info';
import LabelIcon from '@mui/icons-material/Label';
import PeopleIcon from '@mui/icons-material/People';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';

import { businessSelectors } from 'app-components/flux/business';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type {
  ChartOfAccountNodeProps,
  ChartOfAccountProps,
} from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { customersSelectors } from 'app-components/flux/customers';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { projectsSelectors } from 'app-components/flux/projects';
import type { Tag } from 'app-components/flux/tags/tagsTypes';
import type { SplitItemProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import QAmountField from 'components/QAmountField';
import UsageAndEntityIcon from 'components/UsageAndEntityIcon';

import { lookupTagName } from 'data/transactions/utils';
import { OtherSideField } from '../Transactions/OtherSideField';
import {
  isLinkedBillableSplitItem,
  isOverflowSplitItem,
  shouldDisplayOverflowAmount,
  splitHasTransferReference,
} from 'app-components/flux/transactions/splitsHelpers';
import { useSplitsViewStyles } from './splitsViewStyles';
import { useLinkedInvoiceForSplit } from 'app-components/hooks/transactions/useLinkedInvoiceForSplit';
import { getLength } from 'app-components/utils/core/immutableHelpers';
import { useOpenInvoice } from 'containers/SalesAndBilling/invoiceHooks';
import { formatSplitItemName } from 'app-components/flux/transactions/splitsHelpers';

const getCoaNode = (
  coaId: string,
  coaNodesById: OrderedMap<string, ChartOfAccountNodeProps>,
  value?: ChartOfAccountProps | null,
) => {
  let ret = coaNodesById ? coaNodesById.get(coaId) : null;

  if (chartOfAccountsUtils.isTransferCoaNotFound(value)) {
    ret = coaNodesById.get(chartOfAccountsUtils.TRANSFER_NOT_FOUND_COA_ID);
  }
  return ret;
};

type SplitLineMemoProps = {
  memo: string;
  rowIndex: number;
  qbpEnabled: boolean;
  /** Drawer: stack the More/Less toggle below the note text instead of inline to its right. */
  drawerLayout?: boolean;
};

const SplitLineMemo = ({
  memo,
  rowIndex,
  qbpEnabled,
  drawerLayout = false,
}: SplitLineMemoProps): JSX.Element | null => {
  const { classes, cx } = useSplitsViewStyles();
  const [expanded, setExpanded] = useState(false);
  const [canToggle, setCanToggle] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const trimmed = memo.trim();

  useLayoutEffect(() => {
    if (!trimmed) {
      setCanToggle(false);
      return;
    }
    if (expanded) {
      setCanToggle(true);
      return;
    }
    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const el = textRef.current;
        if (!el) {
          setCanToggle(false);
          return;
        }
        setCanToggle(el.scrollHeight > el.clientHeight + 1);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [trimmed, expanded]);

  if (!trimmed) return null;

  const noteField = (
    <TextField
      id={`${rowIndex}-split-row-memo-text`}
      inputRef={textRef}
      value={trimmed}
      multiline
      minRows={1}
      maxRows={expanded ? undefined : 3}
      variant='standard'
      margin='none'
      fullWidth
      className={classes.memoText}
      InputProps={{
        readOnly: true,
        disableUnderline: true,
        sx: (theme) => ({
          ...theme.typography.paragraphRegular,
          color: theme.palette.text.primary,
          alignItems: 'flex-start',
          minWidth: 0,
        }),
      }}
    />
  );

  const toggle = canToggle ? (
    <Button
      id={`${rowIndex}-split-row-memo-toggle`}
      variant='text'
      color='primary'
      className={drawerLayout ? classes.memoToggleBelow : classes.memoToggle}
      onClick={() => setExpanded(!expanded)}
    >
      {expanded ? 'Less' : 'More'}
    </Button>
  ) : null;

  return (
    <div id={`${rowIndex}-split-row-memo`} className={cx(classes.splitRowMemo, qbpEnabled && classes.splitRowMemoQbp)}>
      <EditNote
        aria-hidden
        sx={(theme) => ({
          fontSize: 24,
          flexShrink: 0,
          color: theme.palette.text.secondary,
        })}
      />
      {/* Drawer: stack note text + toggle so More/Less sits below the note; modal keeps them inline. */}
      {drawerLayout ? (
        <div className={classes.memoBody}>
          {noteField}
          {toggle}
        </div>
      ) : (
        <>
          {noteField}
          {toggle}
        </>
      )}
    </div>
  );
};

type SplitViewInvoiceLinkProps = {
  split: SplitItemProps;
  rowIndex: number;
};

const SplitViewInvoiceLink = ({ split, rowIndex }: SplitViewInvoiceLinkProps): JSX.Element | null => {
  // Keep Transaction Details mounted beneath Invoice so closing Invoice
  // returns to the existing drawer without replaying its entrance animation.
  const handleOpenInvoice = useOpenInvoice('details-dialog', { rootUi: true });
  const linkedInvoice = useLinkedInvoiceForSplit(split);

  if (!linkedInvoice) return null;

  const { invoiceId, label } = linkedInvoice;

  return (
    <ButtonBase
      id={`split-${rowIndex}-view-invoice`}
      aria-label={label}
      sx={{ color: 'primary.main', alignSelf: 'flex-start' }}
      onClick={() => handleOpenInvoice({ invoiceId })}
    >
      <Typography variant='paragraphRegular' sx={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <ChevronRightIcon sx={{ fontSize: 18 }} aria-hidden />
    </ButtonBase>
  );
};

type Props = {
  items: SplitItemProps[];
  txnId?: string | null;
  goToOtherSide?: (txn: TransactionProps) => void;
  variant: 'default' | 'series';
  /**
   * Drawer redesign: render each split as a single-column stack with icon-prefixed sections
   * (tag icon + chips, people icon + client/project, note icon + memo) instead of the modal's
   * multi-column grid. Same data/actions; presentation only. Off (modal/series) is unchanged.
   */
  drawerLayout?: boolean;
};

const SplitItemsList = ({ items, txnId, goToOtherSide, variant, drawerLayout = false }: Props): JSX.Element => {
  const { classes, cx } = useSplitsViewStyles();
  const [showAll, setShowAll] = useState(false);
  const [showAllOptionAvailable, setShowAllOptionAvailable] = useState(false);

  const coaIDs = useAppSelector((state) => chartOfAccountsSelectors.getChartOfAccountNodesById(state));
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const projects = useAppSelector(projectsSelectors.getProjects);
  const customers = useAppSelector(customersSelectors.getCustomers);
  const businesses = useAppSelector(businessSelectors.getResourcesById);

  const displayItems = useMemo(() => {
    setShowAllOptionAvailable(items.length > 4);
    return showAll ? items : items.slice(0, 4);
  }, [items, showAll]);

  const renderExclusionCluster = (
    showSp: boolean,
    showReports: boolean,
    options: { index: number; useSmallIcons: boolean },
  ): JSX.Element | null => {
    const { index, useSmallIcons } = options;
    if (!showSp && !showReports) return null;
    const iconClass = useSmallIcons ? classes.exclusionIconSmall : classes.excludeIcon;
    return (
      <div id={`${index}-split-row-exclusion-cluster`} className={useSmallIcons ? classes.exclusionCluster : undefined}>
        {showSp ? (
          <Tooltip title='Excluded from Spending Plan'>
            <ExcludeFromSpendingPlan className={iconClass} id={`split-sp-icon-${index}`} />
          </Tooltip>
        ) : null}
        {showReports ? (
          <Tooltip title='Excluded from Reports'>
            <ExcludeFromReports className={iconClass} id={`split-reports-icon-${index}`} />
          </Tooltip>
        ) : null}
      </div>
    );
  };

  return (
    <div id={`split-items-list--${variant}`} key='splitView' className={cx(classes.splitPadding)}>
      {displayItems.map((splitItem, index) => {
        const coaId = chartOfAccountsUtils.getCoaId(splitItem.coa);
        const catName = getCoaNode(coaId, coaIDs, splitItem.coa)?.fullPathNameWithoutUsageType;
        const splitName = formatSplitItemName(splitItem.name);
        const primaryLabel = splitName || catName;
        const showCategoryContext = Boolean(splitName && catName && splitName !== catName);
        // Drawer: category leads (bold), receipt/item name drops to the secondary line — the inverse
        // of the modal's `primaryLabel`. Falls back to the receipt name when there's no category.
        const drawerPrimaryLabel = catName || splitName;
        const drawerSecondaryLabel = splitName && splitName !== drawerPrimaryLabel ? splitName : null;
        const business = businesses.get(splitItem.businessId ?? '');
        const isOverflow = isOverflowSplitItem(splitItem);
        const supportedTransfer =
          txnId &&
          splitItem?.coa &&
          splitItem.coa.type === 'ACCOUNT' &&
          chartOfAccountsUtils.isSupportedTransferCoa(splitItem.coa);
        const splitLineMustBeExcluded = Boolean(chartOfAccountsUtils.isTransferCOA(splitItem.coa));
        const showExcludeFromSpendingPlanIcon = splitItem.isExcludedFromF2S ?? splitLineMustBeExcluded;
        const showExcludeFromReportsIcon = splitItem.isExcludedFromReports ?? splitLineMustBeExcluded;

        const customer = businessEnabled && splitItem.customerId ? customers?.get(splitItem.customerId) : undefined;
        const customerDisplayName = customer
          ? customer.businessName || `${customer.firstName} ${customer.lastName}`
          : '';
        const project = businessEnabled && splitItem.projectId ? projects.get(splitItem.projectId) : undefined;
        const projectName = project?.name ?? '';
        const showClientProject = Boolean(customer || project);

        const useSmallExclusionIcons = true;

        const renderAmountBlock = (): JSX.Element => {
          if (isOverflow && !shouldDisplayOverflowAmount(splitItem)) {
            return (
              <div id={`${index}-split-row-overflow-amount`} className={classes.overflowAmount}>
                {renderExclusionCluster(showExcludeFromSpendingPlanIcon, showExcludeFromReportsIcon, {
                  index,
                  useSmallIcons: useSmallExclusionIcons,
                })}
                <Tooltip title='Any extra amount is assigned to this Split.'>
                  <InfoIcon className={classes.infoIcon} />
                </Tooltip>
                <Typography variant='bodyRegular'>Overflow</Typography>
              </div>
            );
          }

          return (
            <div id={`${index}-split-row-amount-row`} className={classes.splitRowAmountInline}>
              {renderExclusionCluster(showExcludeFromSpendingPlanIcon, showExcludeFromReportsIcon, {
                index,
                useSmallIcons: useSmallExclusionIcons,
              })}
              <QAmountField
                margin='none'
                classes={{ input: classes.inputField }}
                textFieldVariant='filled'
                editable={false}
                plain
                value={splitItem.amount ? splitItem.amount : 0}
                id={`split-${index}-amount`}
                showSign='positive'
                showColor='positive'
              />
            </div>
          );
        };

        const row1UsageIcon = businessEnabled ? (
          <div id={`${index}-split-row-usage-col`} className={classes.splitRowUsageCol}>
            <div className={classes.padding}>
              <UsageAndEntityIcon
                color={business?.color ?? ''}
                size='xsmall'
                usageIcon={splitItem.businessId ? (business?.taxGroup ?? 'BUSINESS') : 'PERSONAL'}
              />
            </div>
          </div>
        ) : null;

        const row1Category = (
          <div id={`${index}-split-row-category-col`} className={classes.splitRowCategoryCol}>
            <div id={`${index}-split-row-category-stack`} className={classes.splitRowCategoryStack}>
              <Typography variant='bodyRegular' className={classes.categoryText}>
                {primaryLabel}
              </Typography>
              {showCategoryContext ? (
                <Typography variant='captionRegular' color='text.secondary' className={classes.categoryText}>
                  {catName}
                </Typography>
              ) : null}
              {supportedTransfer && goToOtherSide ? (
                <OtherSideField
                  coa={splitItem.coa}
                  txnId={txnId}
                  transferId={splitItem?.transfer?.id}
                  altAmount={splitItem.amount ?? 0}
                  goToOtherSide={goToOtherSide}
                  isSplit
                />
              ) : null}
              {businessEnabled && isLinkedBillableSplitItem(splitItem) && !splitHasTransferReference(splitItem) ? (
                <SplitViewInvoiceLink split={splitItem} rowIndex={index} />
              ) : null}
            </div>
          </div>
        );

        const hasClientProjectCol = businessEnabled && showClientProject;
        const row1ClientProject = hasClientProjectCol ? (
          <div id={`${index}-split-row-client-project`} className={classes.splitRowClientProjectCol}>
            {customer ? <Typography variant='bodyMedium'>{customerDisplayName}</Typography> : null}
            {projectName ? <Typography variant='paragraphRegular'>{projectName}</Typography> : null}
          </div>
        ) : null;

        const row1Amount = (
          <div
            id={`${index}-split-row-amount-column`}
            className={drawerLayout ? classes.drawerAmountCol : classes.splitRowAmountCol}
          >
            {renderAmountBlock()}
            {businessEnabled && splitItem.isBillable ? (
              <Chip id={`${index}-split-row-billable`} label='Billable' size='small' className={classes.billableChip} />
            ) : null}
          </div>
        );

        const row2Tags =
          getLength(splitItem?.tags) > 0 ? (
            <div
              id={`${index}-split-row-tags`}
              className={cx(classes.splitRowTags, businessEnabled && classes.splitRowTagsQbp)}
            >
              {splitItem?.tags?.map((tag: Tag) => {
                const tagName = lookupTagName(tag);
                return <Chip key={tagName} label={tagName} className={classes.chip} />;
              })}
            </div>
          ) : null;

        const row3Memo = splitItem.memo?.trim() ? (
          <SplitLineMemo rowIndex={index} memo={splitItem.memo ?? ''} qbpEnabled={businessEnabled} />
        ) : null;

        const renderSplitItem = () => {
          return (
            <div id={`${index}-split-row`} className={classes.splitRow}>
              <div id={`${index}-split-row-inner`} role='presentation'>
                <div className={classes.rowRootDetail}>
                  <div
                    id={`${index}-split-row-line-1`}
                    className={cx(
                      classes.splitRowLine1,
                      businessEnabled
                        ? hasClientProjectCol
                          ? classes.splitRowLine1ThreeColQbp
                          : classes.splitRowLine1TwoColQbp
                        : hasClientProjectCol
                          ? classes.splitRowLine1ThreeCol
                          : classes.splitRowLine1TwoCol,
                    )}
                  >
                    {row1UsageIcon}
                    {row1Category}
                    {row1ClientProject}
                    {row1Amount}
                  </div>
                  {row2Tags}
                  {row3Memo}
                </div>
              </div>
            </div>
          );
        };

        // Drawer redesign: single-column stack. Category/amount stay as a header row; client &
        // projects and tags become their own icon-prefixed rows (people / label icon), and the
        // note keeps its edit-note icon. Same data, just re-composed for the narrow drawer.
        const renderDrawerSplitItem = () => {
          return (
            <div id={`${index}-split-row`} className={classes.drawerSplitRow}>
              <div id={`${index}-split-row-line-1`} className={classes.drawerPrimaryRow}>
                <div className={classes.drawerPrimaryLeft}>
                  {row1UsageIcon}
                  {/* Drawer category stack: paragraphMedium primary (matches the Figma "Rubik Medium"),
                      not the modal's bodyRegular. Same secondary / transfer / invoice sub-rows. */}
                  <div id={`${index}-split-row-category-stack`} className={classes.drawerCategoryStack}>
                    <Typography variant='paragraphMedium' className={classes.categoryText}>
                      {drawerPrimaryLabel}
                    </Typography>
                    {drawerSecondaryLabel ? (
                      <Typography variant='captionRegular' color='text.secondary' className={classes.categoryText}>
                        {drawerSecondaryLabel}
                      </Typography>
                    ) : null}
                    {supportedTransfer && goToOtherSide ? (
                      <OtherSideField
                        coa={splitItem.coa}
                        txnId={txnId}
                        transferId={splitItem?.transfer?.id}
                        altAmount={splitItem.amount ?? 0}
                        goToOtherSide={goToOtherSide}
                        isSplit
                      />
                    ) : null}
                    {businessEnabled &&
                    isLinkedBillableSplitItem(splitItem) &&
                    !splitHasTransferReference(splitItem) ? (
                      <SplitViewInvoiceLink split={splitItem} rowIndex={index} />
                    ) : null}
                  </div>
                </div>
                {row1Amount}
              </div>
              {getLength(splitItem?.tags) > 0 ? (
                <div id={`${index}-split-row-tags`} className={classes.drawerMetaRow}>
                  <LabelIcon className={classes.drawerMetaIcon} aria-hidden />
                  <div className={classes.drawerTagsWrap}>
                    {splitItem?.tags?.map((tag: Tag) => {
                      const tagName = lookupTagName(tag);
                      return <Chip key={tagName} label={tagName} className={classes.drawerTagChip} />;
                    })}
                  </div>
                </div>
              ) : null}
              {hasClientProjectCol ? (
                <div id={`${index}-split-row-client-project`} className={classes.drawerMetaRow}>
                  <PeopleIcon className={classes.drawerMetaIcon} aria-hidden />
                  <div className={classes.drawerClientProjectText}>
                    {customer ? (
                      <Typography variant='paragraphRegular'>{`${customerDisplayName}${projectName ? ',' : ''}`}</Typography>
                    ) : null}
                    {projectName ? (
                      <Typography variant='paragraphRegular' color='text.secondary'>
                        {projectName}
                      </Typography>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {splitItem.memo?.trim() ? (
                // qbpEnabled={false}: the icon leads the row, so no left indent (unlike the modal).
                <SplitLineMemo rowIndex={index} memo={splitItem.memo ?? ''} qbpEnabled={false} drawerLayout />
              ) : null}
            </div>
          );
        };

        return (
          <Stack key={splitItem.id || index}>
            <Stack sx={{ py: 1.5, px: 1 }}>{drawerLayout ? renderDrawerSplitItem() : renderSplitItem()}</Stack>
            {(index !== displayItems.length - 1 || showAll) && <Divider />}
          </Stack>
        );
      })}

      {showAllOptionAvailable && (
        <div id='split-items-list-show-all'>
          {!showAll && <Divider />}
          <Button
            id='show-all-split-row'
            className={classes.showAllButton}
            onClick={() => {
              tracker.track(tracker.events.splitsV2ShowMore, { willShow: showAll ? 'less' : 'more' });
              setShowAll(!showAll);
            }}
          >
            Show {showAll ? 'Less' : 'All'}
            {showAll ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(SplitItemsList);
